import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Play, Receipt } from 'lucide-react';
import { shopAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { btnGhost, btnPrimary, panelCls } from '../components/ui';
import { notifyCartChanged } from './CartPage';

// Stripe success landing: /checkout/success?session_id=… — the session is
// verified WITH Stripe server-side (confirm endpoint); this page just shows
// the outcome. Idempotent: refreshing re-confirms harmlessly.
export default function CheckoutResult() {
  const [params] = useSearchParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const sessionId = params.get('session_id') || '';
    if (!sessionId) {
      setError(t('Missing checkout session.'));
      return;
    }
    shopAPI.confirm(sessionId)
      .then(({ data }) => {
        setResult(data);
        notifyCartChanged();
      })
      .catch((err) => setError(err.response?.data?.detail || t('Could not confirm the payment.')));
  }, [params]);

  if (error) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p className="text-sm text-[var(--lms-danger)]">{error}</p>
        <Link to="/cart" className={`${btnGhost} mt-5`}>{t('Back to cart')}</Link>
      </div>
    );
  }
  if (!result) return <p className="text-sm text-lms-muted">{t('Confirming your payment…')}</p>;

  return (
    <div className="mx-auto max-w-md py-8 text-center">
      <CheckCircle2 size={40} className="mx-auto text-lms-success" />
      <h1 className="mt-4 text-xl font-semibold">{t('You are enrolled!')}</h1>
      <p className="mt-1 text-sm text-lms-muted">{t('Payment confirmed — your courses are ready.')}</p>
      <div className={`${panelCls} mt-6 divide-y divide-[var(--lms-border)] text-left`}>
        {(result.courses || []).map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-4">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.title}</span>
            <Link to={`/learn/${c.slug}`} className={btnPrimary}>
              <Play size={14} />
              {t('Start')}
            </Link>
          </div>
        ))}
      </div>
      <Link to="/my/purchases" className="mt-5 inline-flex items-center gap-1.5 text-sm text-lms-accent hover:brightness-110">
        <Receipt size={14} />
        {t('View purchase history')}
      </Link>
    </div>
  );
}
